import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';

import { CALL_API } from '../middleware/patterndesign-api';
import Definition from '../helper/patternDesignApiDefinition';
import * as types from './actionTypes';
import { ArgumentException } from '../Exceptions';
import { string2color } from '../helper/ColorUtils';
import { resetPatternColors } from './recoloring';
import { hideRecoloringLoader } from './uiActions';
import { getCacheItem, getCacheKey } from '../reducers/queryCache';
import { getCurrentPageNumber } from '../reducers/page';
import { getPatternPerPage } from '../reducers/ui.patterngallery';

export const fetchColors = patternId => ({
    type: types.PD_FETCH_COLORS,
    api: 'patterndesigns',
    [CALL_API]: {
        types: [types.QUERY_MODIFIED, types.COLORS_REQUESTED, types.COLORS_RECEIVED, types.COLORS_REQUEST_FAILED],
        definition: Definition.GET_COLORS,
        query: { id: patternId },
    },
});

export const fetchCategories = (query = {}) => ({
    type: types.PD_FETCH_CAT,
    api: 'patterndesigns',
    [CALL_API]: {
        types: [types.QUERY_MODIFIED, types.CATEGORIES_REQUESTED, types.CATEGORIES_RECEIVED, types.CATEGORIES_REQUEST_FAILED],
        definition: Definition.GET_CATEGORIES,
        query,
    },
});

export const searchPatterns = (query = {}) => ({
    type: types.PD_SEARCH_PATTERNS,
    api: 'patterndesigns',
    [CALL_API]: {
        types: [types.PATTERN_QUERY_MODIFIED, types.PATTERNS_REQUESTED, types.PATTERNS_RECEIVED, types.PATTERNS_REQUEST_FAILED],
        definition: Definition.GET_PATTERNS,
        query,
    },
});

export const fetchPatterns = (query = {}) => ({
    type: types.PD_FETCH_PATTERNS,
    api: 'patterndesigns',
    [CALL_API]: {
        types: [types.PATTERN_QUERY_MODIFIED, types.PATTERNS_REQUESTED, types.PATTERNS_RECEIVED, types.PATTERNS_REQUEST_FAILED],
        definition: Definition.GET_PATTERNS,
        query,
    },
});

export const recolorPattern = (patternid, colors) => ({
    type: types.PD_RECOLOR,
    api: 'patterndesigns',
    [CALL_API]: {
        types: [types.PATTERN_QUERY_MODIFIED, types.RECOLORING_REQUESTED, types.RECOLORING_RECEIVED, types.RECOLORING_REQUEST_FAILED],
        definition: Definition.RECOLOR,
        query: { id: patternid, colors },
    },
});

export const fetchDesigner = designerId => ({
    type: types.PD_FETCH_DESIGNER,
    api: 'patterndesigns',
    [CALL_API]: {
        types: [types.QUERY_MODIFIED, types.DESIGNERINFO_REQUESTED, types.DESIGNERINFO_RECEIVED, types.DESIGNERINFO_REQUEST_FAILED],
        definition: Definition.GET_DESIGNER,
        query: { designerId },
    },
});

export const setQuery = (query = {}, endpoint) => ({
    api: 'patterndesigns',
    type: types.QUERY_MODIFIED,
    query,
    endpoint,
});

export const resetPatternQuery = () => ({
    api: 'patterndesigns',
    type: types.QUERY_RESETTED,
});

/**
 * Gives us the colors for the specified pattern.
 *
 * @param patternid
 * @returns {function(*, *)}
 */
export const loadColors = patternid => async (dispatch, getState) => {
    const colors = ((getState().location.query) || {}).colors ? string2color(((getState().location.query) || {}).colors) : undefined;
    const pattern = getState().stolo.selectedPattern || {};
    const patterns = getState().stolo.entities.patterns;

    const index = patterns.items.indexOf(patternid);
    if (index > -1) {
        if (patterns.entities[patternid].initialColors) {
            dispatch(resetPatternColors(patternid, colors));
        }
    }

    if (pattern.recolorable === false) {
        dispatch(hideRecoloringLoader());
        return Promise.reject();
    }

    if (colors && colors.length > 0) {
        dispatch(resetPatternColors(patternid, colors));
    }

    await dispatch(fetchColors(patternid));
    return Promise.resolve();
};

/**
 * Load a single category.
 *
 * @param Id
 * @returns {function(*, *)}
 */
export function loadCategorie(Id = 340) {
    return (dispatch, getState) => {
        const categorie = getState().stolo.entities.categories[Id];
        if (categorie) {
            return null;
        }
        return dispatch(fetchCategories());
    };
}

/**
 * Loads the subcategories from an topcategory.
 *
 * @returns {function(*, *)}
 */
export function loadSubCategories() {
    return (dispatch, getState) => {
        const { location: { payload: { categoryid } } } = getState();
        const categories = getState().stolo.entities.categories;
        const categorie = categories.entities[categoryid];

        if (categories.didInvalidate === true) {
            dispatch(fetchCategories({
                includeSubcategories: true,
                thumbnailType: 'medium',
            }));
        }

        if (categorie) {
            // We do not need to fetch anything if we have the subcategorie allready in state.
            if (categorie.containsPatterns === false) {
                return Promise.resolve();
            }
            throw ArgumentException('You need to call loadSubCategories with the id of an topcategory');
        }

        // If we have no categories yet, fetch them.
        dispatch(fetchCategories({
            includeSubcategories: true,
            thumbnailType: 'medium',
        }));
    };
}

/**
 * Loads the top categories.
 *
 * @returns {function(*, *)}
 */
export const loadTopCategories = () => (dispatch, getState) => {
    const topCategories = getState().stolo.entities.categories;

        // If the the entities somehow invalidated we have
        // to fetch them.
    if (topCategories.didInvalidate) {
        return dispatch(fetchCategories({
            includeSubcategories: true,
            thumbnailType: 'medium',
        }));
    }

        // Lookup  and serve from cache, if there is one.
    if (topCategories.items && !isEmpty(topCategories.items)) {
        return Promise.resolve();
    }

    return dispatch(fetchCategories({
        includeSubcategories: true,
        thumbnailType: 'medium',
    }));
};

/**
 * Load patterns specified by query.
 *
 * Otherwise query against the api.
 *
 * @param query
 * @returns {function(*, *)}
 */
export const loadPatterns = async query => async (dispatch, getState) => {
        // The middleware decides if we need to fetch against the
        // api or can use the cache.
    await dispatch(fetchPatterns(query));
    const cacheItem = getCacheItem(getState(), query);
    return cacheItem.items;
};

/**
 * Load multiple patterns by ids.
 *
 * @param patternIds
 * @returns {function(*, *)}
 */
export const loadPatternsById = patternIds => async (dispatch, getState) => {
    const state = getState();
    const page = getCurrentPageNumber(state);
    const count = getPatternPerPage(state);
    const patterns = getState().stolo.entities.patterns;

    const uniqueIds = [...new Set(patternIds)];

    const idsToFetch = difference(uniqueIds, patterns.items);

    if (idsToFetch.length > 0) {
        const query = {
            limit: count,
            offset: page ? (page - 1) * count : 0,
            ids: idsToFetch.join(','),
        };
        await dispatch(fetchPatterns(query));
    }

    return Promise.resolve();
};

/**
 * Load the newest patterns from specified designer.
 */
export const loadPatternsFromDesigner = designerid => async (dispatch, getState) => {
    const state = getState();
    const page = getCurrentPageNumber(state);
    const limit = getPatternPerPage(state);

    const query = {
        sort: 'newestFirst',
        limit,
        offset: page ? (page - 1) * limit : 0,
        designerIds: designerid,
    };
    await dispatch(fetchPatterns(query));
    return Promise.resolve();
};

/**
 * Load the newest patterns from specified designer.
 */
export const loadPatternsFromSearch = search => async (dispatch, getState) => {
    const state = getState();
    const page = getCurrentPageNumber(state);
    const limit = getPatternPerPage(state);

    const query = {
        limit,
        offset: page ? (page - 1) * limit : 0,
        text: search,
    };
    await dispatch(fetchPatterns(query));
    return Promise.resolve();
};

/**
 * Load similiar patterns like the one with patternid.
 *
 * @param patternId
 */
export const loadSimilarPatterns = patternId => {
    const query = { moreLikeThis: patternId };
    return fetchPatterns(query);
};

/**
 * Load similiar patterns like the one with patternid from the same designer.
 *
 * @param patternId
 * @param designerId
 */
export const loadSimilarPatternsByDesigner = (patternid, designerid) => {
    const query = { moreLikeThis: patternid, designerIds: designerid };
    return loadPatterns(query);
};

/**
 * Load the newest patterns from patterndesigns.com
 */
export const loadPatternsNewest = () => async (dispatch, getState) => {
    const state = getState();
    const page = getCurrentPageNumber(state);
    const count = getPatternPerPage(state);

    const query = {
        sort: 'newestFirst',
        limit: count,
        offset: page ? (page - 1) * count : 0,
    };

    await dispatch(fetchPatterns(query));
    return Promise.resolve();
};

/**
 * Load the patterns by categorie. The current categorie id comes from the route.
 *
 * @returns {function(*, *)}
 */
export const loadPatternsByCategory = categoryid => (dispatch, getState) => {
    const state = getState();
    const page = getCurrentPageNumber(state);
    const count = getPatternPerPage(state);

    const query = {
        limit: count,
        offset: page ? (page - 1) * count : 0,
        sort: 'newestFirst',
        categoryIds: categoryid,
    };

    return dispatch(fetchPatterns(query));
};

/**
 * Load the favorited patterns.
 *
 * @returns {function(*, *)}
 */
export const loadFavoritesPatterns = () => async (dispatch, getState) => {
    const favorites = getState().stolo.favorites.items;
    const Ids = favorites.map(favorite => favorite.patternid);
    await dispatch(loadPatternsById(Ids));
    return Promise.resolve();
};



// WEBPACK FOOTER //
// ./src/actions/patterndesignActions.js